export interface SeoEntry {
  // Shown as "<title> | <company name>", or alone when the name is missing.
  title: string;
  description: string;
  // Only public entry pages may be indexed. Everything else is behind login
  // or carries customer account data in its URL, and must stay out of search.
  index?: boolean;
}

export type SeoKey =
  | "Dashboard"
  | "Payments"
  | "SubscriptionAndPlans"
  | "SubscriptionDetails"
  | "Documents"
  | "PaymentMethods"
  | "UpdatePaymentMethod"
  | "ActionCenter"
  | "UserProfile"
  | "Notification"
  | "MakePayment"
  | "SupportHelp"
  | "QuickCheckout"
  | "Agreement"
  | "PaymentLink"
  | "Invoice"
  | "Quotes"
  | "Products"
  | "ViewPayment"
  | "Login"
  | "LoginViaCred"
  | "OtpSend"
  | "OtpVerification"
  | "ForgotPassword"
  | "ForgotOtpVerification"
  | "ResetPassword"
  | "SignUp"
  | "SignUpEmailAttached"
  | "SignUpOtpVerification"
  | "SignUpPassword"
  | "SetPassword"
  | "RequestAccess"
  | "AccessRequestSent"
  | "NotFound";

export const seoData: Record<SeoKey, SeoEntry> = {
  //   pages behind login
  Dashboard: {
    title: "Account Dashboard",
    description:
      "See your balance, upcoming payments, and recent activity at a glance. Manage your account and payments from one simple dashboard.",
  },
  Payments: {
    title: "Payment History & Schedule",
    description:
      "Review past payments and see what is scheduled next. Track every transaction and stay ahead of upcoming due dates.",
  },
  SubscriptionAndPlans: {
    title: "Subscriptions & Plans",
    description:
      "View your active subscriptions and payment plans, check their status and next payment date, and manage the plan that fits you.",
  },
  Documents: {
    title: "Account Documents",
    description:
      "View and download your account documents anytime. Keep signed agreements and other important files within easy reach.",
  },
  PaymentMethods: {
    title: "Manage Payment Methods",
    description:
      "Add, update, or remove your saved cards and bank accounts securely to keep your upcoming payments running smoothly.",
  },
  ActionCenter: {
    title: "Action Center",
    description:
      "See payment links, invoices, quotes, and agreements sent to you, check their status and due dates, and complete any that are still open.",
  },
  UserProfile: {
    title: "My Profile",
    description:
      "Update your personal and contact details so your account stays current and you receive important payment updates.",
  },
  Notification: {
    title: "Notifications",
    description:
      "Read alerts about your payments, due dates, and account changes in one place so you never miss an important update.",
  },
  MakePayment: {
    title: "Make a Payment",
    description:
      "Pay your balance now or schedule a future payment using a saved or new payment method. Quick, secure, and easy.",
  },
  SupportHelp: {
    title: "Help & Support",
    description:
      "Find answers to common questions or contact support for help with your payments, plans, and account.",
  },

  //   link pages (the URL identifies a customer's record)
  SubscriptionDetails: {
    title: "Subscription Details",
    description:
      "Review the details of your subscription or payment plan, including frequency, payment method, and next payment date.",
  },
  UpdatePaymentMethod: {
    title: "Update Payment Method",
    description:
      "Securely update the card or bank account on file so your scheduled payments continue without interruption.",
  },
  QuickCheckout: {
    title: "Quick Checkout",
    description:
      "Pay your balance in a few quick steps, with or without signing in. Confirm your details, choose a payment method, and pay securely.",
  },
  Agreement: {
    title: "Review & Sign Your Agreement",
    description:
      "Review the terms of your payment agreement and sign it securely online. Understand your payment obligations before you commit.",
  },
  PaymentLink: {
    title: "Complete Your Payment",
    description:
      "Review the payment details sent to you and pay securely in a few steps using a card or bank account.",
  },
  Invoice: {
    title: "Pay Your Invoice",
    description:
      "Review your invoice details and pay it securely online with a card or bank account, so your account stays on track.",
  },
  Quotes: {
    title: "Review & Pay Your Quote",
    description:
      "Review the items and pricing in your quote, then pay securely online using your preferred payment method.",
  },
  Products: {
    title: "Product Checkout",
    description:
      "Choose the products you need and check out in a few quick, secure steps.",
  },
  ViewPayment: {
    title: "Payment Status",
    description:
      "Check the status and details of your payment. If a payment did not go through, you can make the payment again securely.",
  },

  //   login, sign-up and access pages
  Login: {
    title: "Log In",
    description:
      "Log in securely to your customer portal to view your balance, make payments, and manage your account.",
    index: true,
  },
  LoginViaCred: {
    title: "Log In with Password",
    description:
      "Enter your email and password to securely access your customer portal account and manage your payments.",
  },
  OtpSend: {
    title: "Send Verification Code",
    description:
      "Choose to receive a one-time verification code by text or email to confirm your identity and continue securely.",
  },
  OtpVerification: {
    title: "Verify Your Identity",
    description:
      "Confirm it is you by entering the one-time code sent to your phone or email, then continue securely to your account.",
  },
  ForgotPassword: {
    title: "Forgot Password",
    description:
      "Enter your email to receive a verification code and reset your password. If your email has more than one account, add your account ID.",
    index: true,
  },
  ForgotOtpVerification: {
    title: "Verify Password Reset Code",
    description:
      "Enter the password reset code we sent to your email to verify your identity, then create a new password.",
  },
  ResetPassword: {
    title: "Reset Your Password",
    description:
      "Create a new password that meets the security requirements to regain secure access to your account.",
  },
  SignUp: {
    title: "Create an Account",
    description:
      "Create your customer portal account with your email to view balances, make payments, and manage your account anytime.",
    index: true,
  },
  SignUpEmailAttached: {
    title: "Choose Your Account",
    description:
      "Your email is linked to more than one account. Enter your account ID to continue setting up portal access.",
  },
  SignUpOtpVerification: {
    title: "Verify Your Email",
    description:
      "Enter the verification code we sent to your email to confirm it and continue creating your portal account.",
  },
  SignUpPassword: {
    title: "Log In to Existing Account",
    description:
      "This email already has a portal account. Enter your password to log in, or reset it if you have forgotten it.",
  },
  SetPassword: {
    title: "Set Your Password",
    description:
      "Choose a secure password that meets the requirements to finish setting up your customer portal account.",
  },
  RequestAccess: {
    title: "Request Account Access",
    description:
      "Can't log in to your account? Submit a short request with your contact details and we will review it and reply by email.",
    index: true,
  },
  AccessRequestSent: {
    title: "Access Request Submitted",
    description:
      "Your access request was submitted. We will review it and notify you by email once your account access is approved.",
  },

  NotFound: {
    title: "Page Not Found",
    description:
      "This page does not exist or the link is invalid or expired. Return to the customer portal to continue.",
  },
};
